import { SentryError } from '@ifixit/sentry';
import { createContext, useMemo, useContext } from 'react';
import type { PropsWithChildren } from 'react';
import type { CountryCode, LanguageCode, Sdk as StorefrontClient } from './generated/sdk';
import { getClientShopifyStorefrontSdk } from './sdk';

export { type Sdk as StorefrontClient } from './generated/sdk';

const Context = createContext<{
   client: StorefrontClient;
   country: CountryCode;
   currencyCode: string;
   language: LanguageCode;
   shopDomain: string;
   storeCode: string;
   storefrontAccessToken: string;
   storeUrl?: string;
   cartUrl?: string;
} | null>(null);

export type ShopifyStorefrontContextProviderProps = PropsWithChildren<{
   country: CountryCode;
   currencyCode: string;
   language: LanguageCode;
   shopDomain: string;
   storeCode: string;
   storefrontAccessToken: string;
   storeUrl?: string;
   cartUrl?: string;
}>;

export function ShopifyStorefrontProvider({
   country,
   currencyCode,
   language,
   shopDomain,
   storeCode,
   storefrontAccessToken,
   storeUrl,
   cartUrl,
   children,
}: ShopifyStorefrontContextProviderProps) {
   const client = useMemo(
      () => ({
         client: getClientShopifyStorefrontSdk({ shopDomain, storefrontAccessToken }),
         country,
         currencyCode,
         language,
         shopDomain,
         storeCode,
         storefrontAccessToken,
         storeUrl,
         cartUrl,
      }),
      [
         country,
         currencyCode,
         language,
         shopDomain,
         storeCode,
         storefrontAccessToken,
         storeUrl,
         cartUrl,
      ]
   );
   return <Context.Provider value={client}>{children}</Context.Provider>;
}

export function useShopifyStorefrontClient() {
   const client = useContext(Context);
   if (client == null) {
      throw new SentryError(
         'useShopifyStorefrontClient must be used within a ShopifyStorefrontProvider'
      );
   }
   return client;
}
